'use client'

import { useState, useEffect } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { useEvents } from '../hooks/useEvents'
import { useClients } from '@/features/clients/hooks/useClients'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@planner/ui'
import { Input } from '@planner/ui'
import { Label } from '@planner/ui'
import { Textarea } from '@planner/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@planner/ui'
import { useTranslation } from 'react-i18next'
import { Plus } from 'lucide-react'
import type { Event, Client, SubscriptionPlan } from '@planner/types'
import { UpsellDialog } from '@/features/subscriptions/components/UpsellDialog'
import { isPlanEqualOrHigher } from '@/features/subscriptions/planConfig'
import { useSubscription } from '@/features/subscriptions/hooks/useSubscription'

interface EventFormProps {
  event?: Event | null
  onClose: () => void
  initialDate?: string
  initialClientId?: string
}

export function EventForm({ event, onClose, initialDate, initialClientId }: EventFormProps) {
  const router = useRouter()
  const searchParams = useSearchParams()
  const { createEvent, updateEvent } = useEvents()
  const { clients, fetchClients } = useClients()
  const { user, userRole } = useAuth()
  const { subscription } = useSubscription()
  const { t } = useTranslation()
  const [formData, setFormData] = useState({
    client_id: '',
    title: '',
    description: '',
    event_date: initialDate || '',
    event_end_date: '',
    number_of_guests: 1,
    status: 'nowa' as Event['status'],
    menu_package_id: '',
    notes: '',
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [upsellOpen, setUpsellOpen] = useState(false)
  const [requiredPlan, setRequiredPlan] = useState<SubscriptionPlan>('starter')

  useEffect(() => {
    // Check for clientId in URL params (from returning from client creation)
    const clientIdFromUrl = searchParams.get('clientId')
    const clientIdToUse = clientIdFromUrl || initialClientId

    if (event) {
      setFormData({
        client_id: clientIdToUse || event.client_id,
        title: event.title,
        description: event.description || '',
        event_date: new Date(event.event_date).toISOString().slice(0, 16),
        event_end_date: event.event_end_date ? new Date(event.event_end_date).toISOString().slice(0, 16) : '',
        number_of_guests: event.number_of_guests,
        status: event.status,
        menu_package_id: event.menu_package_id || '',
        notes: event.notes || '',
      })
    } else if (initialDate || clientIdToUse) {
      setFormData(prev => ({
        ...prev,
        event_date: initialDate || prev.event_date,
        client_id: clientIdToUse || prev.client_id,
      }))
    }

    // Fetch clients if we have a clientId from URL to ensure it's in the list
    if (clientIdFromUrl) {
      fetchClients()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event, initialDate, initialClientId, searchParams])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    const eventData = {
      ...formData,
      event_date: new Date(formData.event_date).toISOString(),
      event_end_date: formData.event_end_date ? new Date(formData.event_end_date).toISOString() : null,
      created_by: user?.id || '',
    }

    const result = event
      ? await updateEvent(event.id, eventData)
      : await createEvent(eventData as any)

    if (result.error) {
      setError(result.error)
      if ((result as any).code === 'PLAN_LIMIT') {
        const rp = (result as any).requiredPlan as SubscriptionPlan | undefined
        const planToRequire = rp || 'starter'
        const currentPlan = (subscription?.plan_type || 'free') as SubscriptionPlan
        
        // Only show upsell if current plan is lower than required plan
        if (!isPlanEqualOrHigher(currentPlan, planToRequire)) {
          setRequiredPlan(planToRequire)
          setUpsellOpen(true)
        }
      }
      setLoading(false)
    } else {
      onClose()
    }
  }

  const handleAddClientClick = () => {
    // Determine return URL based on whether we're editing or creating
    const returnTo = event 
      ? `/events/${event.id}/edit`
      : '/events/new'
    
    // Build query string
    const params = new URLSearchParams()
    params.set('returnTo', returnTo)
    if (event) {
      params.set('eventId', event.id)
    }
    
    router.push(`/clients/new?${params.toString()}`)
  }

  return (
    <>
      <UpsellDialog
        open={upsellOpen}
        onOpenChange={setUpsellOpen}
        reason="eventLimit"
        requiredPlan={requiredPlan}
        currentPlan={(subscription?.plan_type || 'free') as SubscriptionPlan}
        isAdmin={userRole === 'admin'}
      />
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="title">
            Tytuł *
          </Label>
          <Input
            id="title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            required
            placeholder="Nazwa wydarzenia"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="client_id">
            Klient *
          </Label>
          <div className="flex gap-2">
            <Select
              value={formData.client_id}
              onValueChange={(value) => setFormData({ ...formData, client_id: value })}
              required
            >
              <SelectTrigger id="client_id" className="flex-1">
                <SelectValue placeholder="Wybierz klienta" />
              </SelectTrigger>
              <SelectContent>
                {clients.map((client: Client) => {
                  const contactCount = client.client_contacts?.length || 0
                  return (
                    <SelectItem key={client.id} value={client.id}>
                      {client.full_name}
                      {contactCount > 0 && (
                        <span className="text-xs text-muted-foreground ml-2">
                          ({contactCount} {contactCount === 1 ? 'kontakt' : 'kontakty'})
                        </span>
                      )}
                    </SelectItem>
                  )
                })}
              </SelectContent>
            </Select>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={handleAddClientClick}
              title="Dodaj nowego klienta"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="event_date">
            Data i godzina rozpoczęcia *
          </Label>
          <Input
            id="event_date"
            type="datetime-local"
            value={formData.event_date}
            onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="event_end_date">
            Data i godzina zakończenia
          </Label>
          <Input
            id="event_end_date"
            type="datetime-local"
            value={formData.event_end_date}
            onChange={(e) => setFormData({ ...formData, event_end_date: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="number_of_guests">
            Liczba gości *
          </Label>
          <Input
            id="number_of_guests"
            type="number"
            min="1"
            value={formData.number_of_guests}
            onChange={(e) => setFormData({ ...formData, number_of_guests: parseInt(e.target.value) })}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="status">
            Status
          </Label>
          <Select
            value={formData.status}
            onValueChange={(value) => setFormData({ ...formData, status: value as Event['status'] })}
          >
            <SelectTrigger id="status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="nowa">Nowa</SelectItem>
              <SelectItem value="W trakcie">W trakcie</SelectItem>
              <SelectItem value="potwierdzona">Potwierdzona</SelectItem>
              <SelectItem value="zakonczona">Zakończona</SelectItem>
              <SelectItem value="anulowana">Anulowana</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="description">
            Opis
          </Label>
          <Textarea
            id="description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Opis wydarzenia..."
            rows={4}
          />
        </div>
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="notes">
            Notatki
          </Label>
          <Textarea
            id="notes"
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder="Dodatkowe notatki..."
            rows={3}
          />
        </div>
      </div>
      {error && (
        <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 p-4 rounded-md">
          {error}
        </div>
      )}
      <div className="flex justify-end gap-3 pt-6 pb-6 mt-6 border-t">
        <Button type="button" variant="outline" onClick={onClose}>
          {t('common.cancel', 'Anuluj')}
        </Button>
        <Button type="submit" disabled={loading}>
          {loading ? t('common.loading', 'Zapisywanie...') : t('common.save', 'Zapisz')}
        </Button>
      </div>
      </form>
    </>
  )
}

